import React, { useState, useEffect } from 'react';
import Paginator from '../../../../../utils/Paginator';
import { FaTrashAlt, FaPlus } from 'react-icons/fa';
import { Venue } from '../../../../../api/apiTypes';
import { listVenues } from '../../../../../api/functionApi';
import Swal from 'sweetalert2';
import { useAxiosInterceptor } from '../../../../../api/wrappers';

type ListaUnknownMercatiModaleProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  lista: string[]; 
  onRemove: (item: string) => void;
  listaMercati: Venue[]; 
  onAddToVenueList: (venue: Venue) => void;
};

const TrashIcon = FaTrashAlt as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const PlusIcon = FaPlus as React.ComponentType<React.SVGProps<SVGSVGElement>>;

const ListaUnknownMercatiModale: React.FC<ListaUnknownMercatiModaleProps> = ({
  isOpen,
  onClose,
  title,
  lista,
  onRemove,
  onAddToVenueList,
}) => {
    useAxiosInterceptor(); 
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [showAddSection, setShowAddSection] = useState(false);
  const [unknownList, setUnknownList] = useState<string[]>(lista);
  const [availableVenues, setAvailableVenues] = useState<Venue[]>([]);
  const [addedVenues, setAddedVenues] = useState<Venue[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Aggiorna la lista locale quando la prop cambia
  useEffect(() => {
    setUnknownList(lista);
  }, [lista]);

  useEffect(() => {
    if (!isOpen || !showAddSection) return;

const searchVenues = async () => {
  if (search.length >= 3) {
    setIsSearching(true);
    try {
      // Unica chiamata: l'API cerca in OR su tutti i campi
      const results = await listVenues(0, 100, { 
        search_text: search,
        operating_mics: [search.toUpperCase()], // Aggiunge anche la ricerca per MIC esatto
      });
      
      // Filtra escludendo quelli già nella lista unknown
      const filtered = results.filter(
        (venue) => !lista.includes(venue.market_name)
      );
      
      setAvailableVenues(filtered);
    } catch (error) {
      console.error('Errore nella ricerca delle venues:', error);
      setAvailableVenues([]);
    } finally {
      setIsSearching(false);
    }
  } else {
    setAvailableVenues([]);
  }
};

    const timeoutId = setTimeout(searchVenues, 500);
    return () => clearTimeout(timeoutId);
  }, [search, isOpen, showAddSection, lista]);

  const handleAddVenue = (venue: Venue) => {
    onAddToVenueList(venue);
    setAddedVenues(prev => [...prev, venue]);
    setAvailableVenues(prev => prev.filter(v => v.id !== venue.id));
  };

  const handleAddFromUnknown = (item: string) => {
    const newVenue: Venue = {
      id: Date.now().toString() + Math.random().toString(36).substring(2),
      market_name: item,
      mic: '',
      operating_mic: '',
      legal_entity_name: '',
      acronym: '',
      status: '',
      validated: false,
      CountryCode: '',
      City: '',
      MarketCategory: '',
    };

    handleAddVenue(newVenue);
  };

  const handleAddAllUnknown = () => {
    if (unknownList.length === 0) return;

    const newVenues: Venue[] = unknownList.map(item => ({
      id: Date.now().toString() + Math.random().toString(36).substring(2),
      market_name: item,
      mic: '',
      operating_mic: '',
      legal_entity_name: '',
      acronym: '',
      status: '',
      validated: false,
      CountryCode: '',
      City: '',
      MarketCategory: '',
    }));

    // Aggiungi tutti alla lista globale
    newVenues.forEach(v => onAddToVenueList(v));

    // Aggiorna lo stato interno
    setAddedVenues(prev => [...prev, ...newVenues]);
    setAvailableVenues([]);

    // Svuota lista locale e avvisa il genitore
    unknownList.forEach(item => onRemove(item));
    setUnknownList([]);

    // Mostra conferma
    Swal.fire({
      icon: 'success',
      title: 'Aggiunti con successo',
      text: `${newVenues.length} elementi aggiunti!`,
      timer: 2000,
      showConfirmButton: false,
    });
  };

  const itemsPerPage = 6;
  const totalPages = Math.ceil(unknownList.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUnknown = unknownList.slice(startIndex, endIndex);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-start pt-10">
      <div className="relative p-6 border w-full max-w-5xl shadow-lg rounded-2xl bg-white">
        <div className="flex justify-between items-center pb-6">
          <h3 className="text-2xl font-bold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-3xl">&times;</button>
        </div>

        <div className="bg-surface rounded-lg shadow-md overflow-hidden p-6">
          {paginatedUnknown.length > 0 && (
            <div className="flex justify-end mb-6">
              <button
                onClick={handleAddAllUnknown}
                className="px-4 py-2 bg-textPrimary text-white hover:bg-textSecondary rounded"
              >
                Aggiungi Tutto
              </button>
            </div>
          )}

          {paginatedUnknown.length > 0 ? (
            <ul className="space-y-2 text-textPrimary">
              {paginatedUnknown.map((item, index) => (
                <li key={index} className="flex justify-between items-center border-b pb-2">
                  <span>{item}</span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleAddFromUnknown(item)}
                      className="bg-textPrimary-600 hover:text-red-800 p-2 rounded-full hover:bg-gray-200"
                      title="Aggiungi"
                    >
                      <PlusIcon />
                    </button>
                    <button
                      onClick={() => onRemove(item)}
                      className="bg-textPrimary-600 hover:text-red-800 p-2 rounded-full hover:bg-gray-200"
                      title="Rimuovi"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-4 text-center text-gray-500">Nessun elemento sconosciuto trovato.</div>
          )}
        </div>

        {totalPages > 0 && (
          <div className="p-4 mb-4">
            <Paginator
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        )}

        <div className="flex justify-end mt-4">
          <button
            onClick={() => setShowAddSection(true)}
            className="px-4 py-2 bg-textPrimary text-white hover:bg-textSecondary rounded"
          >
            Aggiungi Mercato
          </button>
        </div>

        {showAddSection && (
          <>
            {addedVenues.length > 0 && (
              <div className="mb-4 p-2 bg-gray-100 rounded-lg max-h-40 overflow-y-auto">
                <h4 className="font-semibold text-gray-700 mb-2">Aggiunti:</h4>
                <ul className="space-y-1">
                  {addedVenues.map((v) => (
                    <li key={v.id} className="flex justify-between items-center text-gray-800">
                      <span>{v.market_name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="pt-6">
              <input
                type="text"
                placeholder="Digita almeno 3 caratteri per cercare..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring focus:ring-gray-300 mb-4"
              />
              {search.length > 0 && search.length < 3 && (
                <p className="text-sm text-gray-500 mb-2">
                  Digita ancora {3 - search.length} carattere{3 - search.length > 1 ? 'i' : ''} per avviare la ricerca
                </p>
              )}
              
              <div className="divide-y divide-divider max-h-[50vh] overflow-y-auto">
                {isSearching ? (
                  <div className="p-4 text-center text-gray-500">
                    Caricamento in corso...
                  </div>
                ) : search.length > 0 && search.length < 3 ? (
                  <div className="p-4 text-center text-gray-500">
                    Digita almeno 3 caratteri per iniziare la ricerca
                  </div>
                ) : availableVenues.length > 0 ? (
                  availableVenues.map((venue) => (
                    <div
                      key={venue.id}
                      className="grid gap-x-4 p-4 hover:bg-gray-50 transition duration-150 ease-in-out items-center grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr_auto]"
                    >
                      <div className="flex items-center text-textPrimary ml-2">{venue.market_name}</div>
                      <div className="flex justify-center items-center text-primary">{venue.mic}</div>
                      <div className="flex justify-center items-center text-primary">{venue.operating_mic}</div>
                      <div className="flex justify-center items-center text-primary">{venue.legal_entity_name}</div>
                      <div className="flex justify-center items-center text-primary">{venue.acronym}</div>
                      <div className="flex justify-center items-center text-primary">{venue.status}</div>
                      <div className="flex justify-center items-center space-x-2">
                        <button
                          onClick={() => handleAddVenue(venue)}
                          className="bg-textPrimary-600 hover:text-red-800 p-2 rounded-full hover:bg-gray-200"
                          title="Aggiungi"
                        >
                          <PlusIcon />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-center text-gray-500">
                    {search.length >= 3 ? 'Nessun mercato trovato con questi criteri' : 'Inizia a digitare per cercare mercati'}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ListaUnknownMercatiModale;